import { type ReactNode } from "react"

import { X, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"

import DragHandle from "./DragHandle"

interface ModalHeaderProps {
  /** The title of the modal */
  title: string
  caption?: string
  ref?: React.Ref<HTMLElement>
  /** Icon to display next to the title */
  icon?: ReactNode
  /** Rendered right after the title text, e.g. a count badge */
  titleAdornment?: ReactNode
  /** Extra classes merged onto the title heading, e.g. a heavier weight */
  titleClassName?: string
  /** Rendered under the title, e.g. a control that expands part of the body */
  subtitle?: ReactNode
  /** Additional content to render in the header (right side) */
  headerActions?: ReactNode
  /** Whether the modal is loading */
  isLoading?: boolean
  /** Whether the drag has crossed the dismiss threshold */
  isPastThreshold: boolean
  /** Progress of the drag from 0 to 1 */
  dragProgress: number
  /** Function to call when the close button is clicked */
  onClose: () => void
  /** Additional content to render in the tab navigation area */
  tabNavigation?: ReactNode
  /** Handler for pointer down events */
  onPointerDown?: (e: React.PointerEvent) => void
  /** Handler for pointer enter events */
  onPointerEnter?: (e: React.PointerEvent) => void
  /** Control visibility of the close button */
  showCloseButton?: boolean
  /** Show the grab bar only when the sheet can actually be dragged closed */
  showDragHandle?: boolean
}

/**
 * Header component for the TabModal with title, actions, and drag handle
 */
export const ModalHeader = ({
  title,
  caption,
  ref,
  icon,
  titleAdornment,
  titleClassName,
  subtitle,
  headerActions,
  isLoading,
  isPastThreshold,
  dragProgress,
  onClose,
  tabNavigation,
  onPointerDown,
  onPointerEnter,
  showCloseButton = true,
  showDragHandle = true
}: ModalHeaderProps) => {
  const { t } = useTranslation()

  return (
    <header
      ref={ref}
      className={cn(
        "sticky top-0 z-50 flex-shrink-0 border-b border-gray-800 bg-gray-900/95 shadow-lg backdrop-blur-md",
        showDragHandle && "cursor-grab active:cursor-grabbing"
      )}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}>
      {showDragHandle && (
        <DragHandle
          isPastThreshold={isPastThreshold}
          dragProgress={dragProgress}
        />
      )}

      {/* Header content */}
      <div className={cn("flex items-center justify-between px-4 pb-3", !showDragHandle && "pt-3")}>
        <div className="flex items-center">
          {icon && <div className="me-2">{icon}</div>}
          <div className="min-w-0">
            {caption && <p className="text-xs font-medium text-gray-400">{caption}</p>}
            <h2
              className={cn(
                "flex items-center gap-2 text-lg text-white",
                caption ? "font-semibold" : "font-medium",
                titleClassName
              )}>
              {isPastThreshold ? t("release-to-close", "Release to close") || "Release to close" : title}
              {!isPastThreshold && titleAdornment}
              {isLoading && (
                <span className="ms-3 inline-flex items-center text-yellow-500">
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t("common-saving", "Saving")}
                </span>
              )}
            </h2>
            {!isPastThreshold && subtitle}
          </div>
        </div>

        {(headerActions || showCloseButton) && (
          <div className="flex items-center gap-2">
            {headerActions}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common:close", "Close")}
                className="relative flex size-7 items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-colors before:absolute before:-inset-2 before:content-[''] hover-device:hover:bg-gray-700 hover-device:hover:text-white">
                <X
                  aria-hidden="true"
                  focusable="false"
                  className="size-4"
                />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tab navigation area */}
      {tabNavigation && <div className="flex border-t border-gray-800 bg-gray-900/95">{tabNavigation}</div>}
    </header>
  )
}

ModalHeader.displayName = "ModalHeader"

export default ModalHeader
